import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import styles from "./animal-species-select.module.css"
import ChooseSomethingIcon from "@/assets/icons/misc/choose-something-icon.tsx"
import OverflowMenuItem from "@/components/task-card/overflow-menu-item.tsx"
import { getSpeciesIcon, type Species } from "@/features/farm"
import { useGetSpeciesQuery } from "@/features/farm/api/species-api.ts"
import AnimalIconCard from "@/features/farm/components/animal-icon-card/animal-icon-card.tsx"
import RequiredIndicator from "@/features/farm/components/required-indicator/required-indicator.tsx"
import useGetCurrentSpecies from "@/features/farm/hooks/use-get-current-species.ts"

interface CategorySelectProps {
  speciesId: string
  onSpeciesIdChange: (selection: Species) => void
  isRequired?: boolean
}

const AnimalSpeciesSelect: React.FC<CategorySelectProps> = ({
  speciesId,
  onSpeciesIdChange,
  isRequired = false,
}) => {
  const { t } = useTranslation()
  const { data: species_breeds } = useGetSpeciesQuery()
  const currentSelectedSpeciesObject: Species | undefined = species_breeds
    ? species_breeds[speciesId]
    : undefined
  const speciesArray = useGetCurrentSpecies()

  const handleCategorySelect = (species: Species) => {
    if (speciesId === species.id) {
      return
    }
    onSpeciesIdChange(species)
  }

  useEffect(() => {
    if (speciesArray.length === 1 && !speciesId) {
      speciesArray[0] && onSpeciesIdChange(speciesArray[0])
    }
  }, [onSpeciesIdChange, speciesArray, speciesId, species_breeds])

  return (
    <OverflowMenuItem isDisabled={speciesArray.length === 1}>
      <div className={styles["card-flex"]}>
        <div className={styles["title-flex"]}>
          <ChooseSomethingIcon />
          <div className={styles["title"]}>{t("category")}</div>
        </div>

        {currentSelectedSpeciesObject && speciesId !== "" && (
          <div className={styles["selected-category"]}>
            {getSpeciesIcon(currentSelectedSpeciesObject.name)}
            <div className={styles["title"]}>
              {t(currentSelectedSpeciesObject.name)}
            </div>
          </div>
        )}
      </div>

      {speciesArray.length > 1 && (
        <div className={styles["categories-section-animal"]}>
          {speciesArray.map((species) => {
            if (species)
              return (
                <AnimalIconCard
                  key={species.id}
                  category={species}
                  onClick={() => handleCategorySelect(species)}
                  isSelected={speciesId === species.id}
                />
              )
          })}
        </div>
      )}
      {isRequired && <RequiredIndicator />}
    </OverflowMenuItem>
  )
}

export default React.memo(AnimalSpeciesSelect)
